<template>
  <div>
    <div class="image-gallery-container" :style="{ height: containerHeight }">
        <!-- 左右滚动箭头 -->
        <div v-if="showNavigation" class="navigation-arrow left-arrow" @click="prevPage">
        <el-icon><ArrowLeft /></el-icon>
        </div>
        <div v-if="showNavigation" class="navigation-arrow right-arrow" @click="nextPage">
        <el-icon><ArrowRight /></el-icon>
        </div>

        <!-- 图片展示区域 -->
        <div class="image-gallery" ref="galleryRef">
        <el-row
            v-for="(page, pageIndex) in paginatedImages"
            :key="pageIndex"
            class="gallery-page"
            v-show="currentPage === pageIndex"
            :style="{ height: pageHeight }"
        >
            <el-col
            v-for="(image, imageIndex) in page"
            :key="imageIndex"
            :span="24 / columns"
            class="gallery-image-col"
            >
            <div class="gallery-image" @click="handleImageClick(image)">
                <el-image
                :src="image.src"
                :alt="image.alt || 'Image'"
                :style="{ width: imageWidth, height: imageHeight }"
                fit="cover"
                />
                <div v-if="image.caption" class="image-caption">{{ image.caption }}</div>
            </div>
            </el-col>
        </el-row>
        </div>
    </div>
    <!-- 分页控件 -->
    <el-pagination
        v-if="showPagination"
        class="gallery-pagination"
        :current-page="currentPage + 1"
        :page-size="1"
        :total="paginatedImages.length"
        @current-change="handlePageChange"
        layout="prev, pager, next"
        small
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue';

const props = defineProps({
  images: {
    type: Array,
    required: true,
    validator: (value) => {
      return value.every((img) => img.src);
    },
  },
  columns: {
    type: Number,
    default: 4,
    validator: (value) => value > 0,
  },
  rows: {
    type: Number,
    default: 3,
    validator: (value) => value > 0,
  },
  imageWidth: {
    type: String,
    default: '100%',
  },
  imageHeight: {
    type: String,
    default: '200px',
  },
  showPagination: {
    type: Boolean,
    default: true,
  },
  showNavigation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['image-click']);

const currentPage = ref(0);
const galleryRef = ref(null);
const containerHeight = ref('auto');
const pageHeight = ref('auto');

const paginatedImages = computed(() => {
  const imagesPerPage = props.columns * props.rows;
  const pages = [];
  for (let i = 0; i < props.images.length; i += imagesPerPage) {
    pages.push(props.images.slice(i, i + imagesPerPage));
  }
  return pages;
});

// 计算固定高度
const calculateFixedHeight = () => {
  if (paginatedImages.value.length > 1) {
    // 如果有多页，固定高度为一页的高度
    const rowHeight = parseFloat(props.imageHeight) || 200; // 默认高度为 200px
    const gap = 20; // 行间距
    containerHeight.value = `${props.rows * (rowHeight + gap)}px`;
    pageHeight.value = '100%';
  } else {
    // 如果只有一页，高度自适应
    containerHeight.value = 'auto';
    pageHeight.value = 'auto';
  }
};

// 监听分页变化
watch(paginatedImages, calculateFixedHeight, { immediate: true });

const handleImageClick = (image) => {
  if (image.link) {
    window.open(image.link, '_blank');
  }
  emit('image-click', image);
};

const handlePageChange = (page) => {
  currentPage.value = page - 1;
};

const nextPage = () => {
  if (currentPage.value < paginatedImages.value.length - 1) {
    currentPage.value += 1;
  }
};

const prevPage = () => {
  if (currentPage.value > 0) {
    currentPage.value -= 1;
  }
};

// 键盘左右箭头切换
const handleKeyDown = (e) => {
  if (e.key === 'ArrowRight') {
    nextPage();
  } else if (e.key === 'ArrowLeft') {
    prevPage();
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
});
</script>

<style scoped>
.image-gallery-container {
  position: relative;
  width: 100%;
  padding: 20px 0;
  transition: height 0.3s ease;
}

.image-gallery {
  width: 100%;
  overflow: hidden;
}

.gallery-page {
  margin-bottom: 20px;
  transition: transform 0.3s ease;
}

.gallery-image-col {
  padding: 10px;
  box-sizing: border-box;
}

.gallery-image {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border-radius: 12px;
  transition: transform 0.3s;
}

.gallery-image:hover {
  transform: scale(1.03);
}

.image-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px;
  text-align: center;
  font-size: 14px;
}

.gallery-pagination {
  display: flex;
  justify-content: center;
}

.navigation-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.navigation-arrow:hover {
  background: rgba(255, 255, 255, 0.9);
}

.left-arrow {
  left: 10px;
}

.right-arrow {
  right: 10px;
}
</style>
